// 🔧 LandingHome.js
// ✅ Features:
// - Fully matches the original index.html visual layout
// - Uses correct JSX and React-friendly syntax
// - Paths for images and assets point to /public/
// - Scripts and jQuery removed (can be replaced with React if needed later)

import React, { useState } from 'react';
import { useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { db } from "../../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { isEventPublic } from "../../utils/event_visibility";
import { logger } from '../../utils/logger';
import QRImage from "../../assets/images/qr.png";
import QR2Image from "../../assets/images/qr2.png";
import CommandCenterImage from "../../assets/images/command-center.png";
import EventControlImage from "../../assets/images/event-control.png";
import VoterExperienceImage from "../../assets/images/voter-experience.png";
import VolunteersImage from "../../assets/images/volunteers.png";
import PricingSection from "../../components/PricingSection";
import { showStatus } from "../../utils/status_helpers";
import SeoHead from "../../components/seo_head";
import { PUBLIC_PAGE_SEO } from "../../constants/marketing_pages";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

const LandingHome = () => {

  const [activeInfo, setActiveInfo] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [allEvents, setAllEvents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const navigate = useNavigate(); 
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [status, setStatus] = useState(null);

  const infoPanels = [
    { id: 1, icon: "business", title: "Easy for voters", text: "Just scan and tap — no app, no account." },
    { id: 2, icon: "transport", title: "Live results", text: "See voting totals update instantly." },
    { id: 3, icon: "technology", title: "Built for events", text: "Whether it’s cars, cakes, pets, or paintings." },
    { id: 4, icon: "search", title: "Paper-free", text: "Say goodbye to manual counting and ballots" }
  ];
  const reasonsList = [
    {
      title: "Works anywhere",
      description: "Reliable at in-person events, pop-ups, or indoor expos. All you need is a phone and a browser."
    },
    {
      title: "No training needed",
      description: "It’s simple enough for kids, guests, and first-timers to vote confidently."
    },
    {
      title: "Setup in minutes",
      description: "Add entries, print QR codes, and you're ready. No tech team required."
    }
  ];

  const testimonialsList = [
    // ✅ Event Managers
    {
      name: "Dalila P.",
      quote: "We used Let's Go Vote for the Bug Run and it saved us hours. No more counting slips or arguments over ballots.",
    },
    {
      name: "Fuli M.",
      quote: "Let's Go Vote let us focus on the fun. The app handled all the voting logistics behind the scenes.",
    },

    {
      name: "Tony V.",
      quote: "Voting with my phone was easy and fun. I actually looked forward to scanning each car!",
    },
    {
      name: "Jasmine P.",
      quote: "I loved being able to vote without downloading anything. Just scan and go. Super smooth!",
    },
    {
      name: "Doug K.",
      quote: "Voting at the Bug Run this year was the best it’s ever been.",
    }
  ];

  const features = [
    {
      img: VoterExperienceImage,
      title: "Simple Voter Experience",
      subtitle: "Voters scan and vote in seconds",
      text: [
        "Each entry gets its own QR code. Attendees simply scan with their phone, select their favorite, and vote — no app downloads or account setup needed.",
        "The voting interface is mobile-friendly and intuitive for all ages. Whether it's a local art show or a food competition, anyone can participate instantly."
      ]
    },
    {
      img: EventControlImage,
      title: "Total Event Control",
      subtitle: "Real-time insights and easy setup",
      text: [
        "Create categories, add entries, and generate printable QR codes with just a few clicks. The admin dashboard makes setup quick and stress-free.",
        "Watch votes come in live or keep results hidden until reveal time. Export results at any moment and maintain full control over the voting experience."
      ]
    },
    {
      img: VolunteersImage, // Use placeholder or create an image
      title: "Empower Your Volunteers",
      subtitle: "Assign tasks, track shifts, and streamline collaboration",
      text: [
        "Add volunteers to your event and assign them to time slots or specific tasks. Each volunteer has a profile and can access only what they need.",
        "Track coverage, make adjustments in real time, and reduce the last-minute scramble. It’s never been easier to coordinate helpers across your event."
      ]
    },
    {
      img: CommandCenterImage, // Use placeholder or create an image
      title: "A Full Command Center",
      subtitle: "Control your entire event from one clean interface",
      text: [
        "The admin dashboard lets you create events, manage categories, register entries, and monitor vote activity — all in one place.",
        "With real-time updates, search, filters, and export tools, everything you need is right at your fingertips. No coding or training required."
      ]
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 📥 Load all events once
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const snapshot = await getDocs(query(collection(db, "events"), where("publicSeo", "==", true)));
        const list = [];
        snapshot.forEach((doc) => {
          const d = doc.data();
          if (d.name && d.slug && isEventPublic(d)) {
            list.push({
              name: d.name,
              slug: d.slug,
              startDate: d.startDate || null,
            });
          }
        });
        setAllEvents(list);
      } catch (err) {
        console.error("Failed to load events", err);
      }
    };
    fetchEvents();
  }, []);

  // 🔎 Filter + show dropdown reactively
  useEffect(() => {
    const input = searchTerm.trim().toLowerCase();

    if (!input) {
      setFiltered([]);
      setShowSuggestions(false);
      return;
    }

    const matches = allEvents.filter(
      (event) =>
        event.name.toLowerCase().includes(input) ||
        event.slug.toLowerCase().includes(input)
    );

    setFiltered(matches.slice(0, 5));
    setShowSuggestions(matches.length > 0);
  }, [searchTerm, allEvents]);

  const handleSuggestionClick = (name) => {
    setSearchTerm(name); // ✅ Event Name shown in input
    setTimeout(() => {
      setShowSuggestions(false);
      setFiltered([]);
    }, 0);
  };


  // 🚀 Handle Go button
  const handleSearchSubmit = (e) => {
    e.preventDefault();

    const match = allEvents.find(
      (e) => e.name.toLowerCase() === searchTerm.toLowerCase()
    );

    if (match) {

      // 📊 Google Analytics
      trackEvent({
        action: GA_EVENTS.EVENT_SEARCH_SUBMITTED,
        parameters: {
          search_term: searchTerm,
          matched: true,
          event_slug: match.slug,
          source_page: window.location.pathname,
        },
      });

      navigate(`/${match.slug}/`); // ✅ uses slug for routing
    } else {

      // 📊 Google Analytics
      trackEvent({
        action: GA_EVENTS.EVENT_SEARCH_NOT_FOUND,
        parameters: {
          search_term: searchTerm,
          matched: false,
          source_page: window.location.pathname,
        },
      });

      showStatus(setStatus, {
        type: "error",
        message: "Event not found.",
      });

    }
  };

const homeStructuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Let's Go Vote",
    url: "https://letsgovote.app",
    logo: "https://letsgovote.app/images/social-share-default.png",
    sameAs: [
      "https://www.facebook.com/letsgovoteapp",
      "https://www.instagram.com/letsgovoteapp/",
      "https://www.linkedin.com/company/132004035/admin/dashboard/",
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Let's Go Vote",
    applicationCategory: "EventManagementApplication",
    operatingSystem: "Web",
    url: "https://letsgovote.app",
    description:
      "Let's Go Vote is a QR code voting and event management platform for car shows, art shows, pet contests, school elections, silent auctions, sponsors, volunteers, vendors, and community events.",
  },
];

const homeSeo = {
  ...PUBLIC_PAGE_SEO.home,
  structuredData: homeStructuredData,
};

return (
  <>
    <SeoHead {...homeSeo} />
      {/* Hero Section */}
      <section id="top-content" className="container-fluid">

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
            role="alert"
          >
            {status.message}
          </div>
        )}

        <div className="container">
          <div className="row">
          
            <div className="col-xs-12 text-center phone-animation-holder">
              <div className="phone-animation">
                <div className="phone">
                  <img src={QR2Image} height="250" alt="Phone" />
                  <span className="phone-line rline1"></span>
                  <span className="phone-line rline2"></span>
                  <span className="phone-line rline3"></span>
                </div>
                <div className="cloud cloud1"><img src={QRImage} width="60" height="60" alt="Cloud" /></div>
                <div className="cloud cloud2"><img src={QRImage} width="60" height="60" alt="Cloud" /></div>
                <div className="cloud cloud3"><img src={QRImage} width="60" height="60" alt="Cloud" /></div>
              </div>

              <h1>QR Code Voting for Events</h1>
              <h2>Scan. Vote. See results.</h2>
              <h4>
                Let's Go Vote makes public voting easy for car shows, art shows,
                pet contests, food competitions, school elections, community awards, auctions,
                and more.
              </h4>

              {/* =====================================================
                  🚀 Organizer CTA Buttons
                  Purpose:
                  - Gives event organizers a clear path to choose a paid event plan
                  - Sends new organizers to the event setup / checkout intake flow
                  - Keeps Dashboard available for returning users
              ===================================================== */}
              <div className="mt-4 d-flex flex-column flex-md-row gap-2 justify-content-center">
                <a
                  href="#pricing"
                  className="btn btn-lg btn-success px-4 py-2"
                >
                  🚀 Start Your Event
                </a>

                <Link
                  to="/dashboard"
                  className="btn btn-lg btn-outline-light px-4 py-2"
                >
                  Dashboard
                </Link>
              </div>

            </div>
          </div>
        </div>
      </section>

      <section className="container-fluid py-5 bg-light">
        <div className="container" style={{ maxWidth: "900px" }}>
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4 p-md-5 text-center">

              <h3 className="fw-bold mb-2">
                🔎 Attending an Event?
              </h3>

              <p className="text-muted mb-4">
                Search for your event page to vote, view entries, see sponsors,
                check schedules, and follow results.
              </p>

              <form onSubmit={handleSearchSubmit}>
                <div className="row g-2 justify-content-center position-relative">
                  <div className="col-12 col-md-9 position-relative">
                    <input
                      className="form-control form-control-lg"
                      type="text"
                      placeholder="Start typing the event name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      autoComplete="off"
                    />

                    {showSuggestions && filtered.length > 0 && (
                      <ul
                        className="list-group position-absolute w-100 shadow-sm"
                        style={{
                          top: "100%",
                          left: 0,
                          zIndex: 1000,
                        }}
                      >
                        {filtered.map((e) => (
                          <li
                            key={e.slug}
                            className="list-group-item list-group-item-action"
                            onMouseDown={() => handleSuggestionClick(e.name)}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="fw-semibold">
                                {e.name}
                              </span>

                              <small className="text-muted">
                                {(() => {
                                  if (!e.startDate) return "";

                                  let date;

                                  // Firestore Timestamp
                                  if (typeof e.startDate?.toDate === "function") {
                                    date = e.startDate.toDate();
                                  }
                                  // Timestamp object
                                  else if (e.startDate.seconds) {
                                    date = new Date(e.startDate.seconds * 1000);
                                  }
                                  // String / Date
                                  else {
                                    date = new Date(e.startDate);
                                  }

                                  return isNaN(date.getTime())
                                    ? ""
                                    : date.toLocaleDateString(undefined, {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      });
                                })()}
                              </small>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="col-12 col-md-3">
                    <button
                      className="btn btn-primary btn-lg w-100"
                      type="submit"
                      disabled={!searchTerm.trim()}
                    >
                      Go
                    </button>
                  </div>
                </div>
              </form>

              <div className="small text-muted mt-3">
                Event organizers should use <strong>Start Your Event</strong> above.
                This search is for attendees looking for an existing event.
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          🎯 Perfect For Section
          Purpose:
          - Quickly tells visitors what kinds of events Let's Go Vote supports
          - Helps future SEO landing pages align with visible homepage categories
          - Gives event organizers immediate confidence that the app fits their use case
      ===================================================== */}
      <section className="container-fluid bg-light text-dark">
        <div className="container">
          <div className="row text-center mb-4">
            <div className="col-12">
              <h4 className="fw-bold">Perfect For</h4>
              <p className="text-muted mb-0">
                Flexible QR voting for contests, shows, awards, judging, and community events.
              </p>
            </div>
          </div>

          <div className="row g-3 text-center">
            {[
              { icon: "🚗", title: "Car Shows", path: "/car-shows" },
              { icon: "🎨", title: "Art Shows", path: "/art-shows" },
              { icon: "🐶", title: "Pet Shows", path: "/pet-shows" },
              { icon: "🗳️", title: "School Elections", path: "/school-elections" },
              { icon: "🏷️", title: "Silent Auctions", path: "/silent-auctions" },
              { icon: "📱", title: "QR Code Voting", path: "/qr-code-voting" },
            ].map((item) => (
              <div className="col-6 col-md-4 col-lg-2" key={item.title}>

                <Link
                  to={item.path}
                  className="card h-100 border-0 shadow-sm p-3 text-decoration-none text-dark"
                >
                  <div className="display-6">{item.icon}</div>
                  <div className="fw-bold mt-2">{item.title}</div>
                </Link>

              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          💵 Pricing Section
          Purpose:
          - Shows available paid event tiers directly on the homepage
          - Helps organizers compare plans before starting the event setup flow
          - Keeps pricing controlled by the PricingSection / pricing tier JSON
      ===================================================== */}
      <PricingSection />

      {/* =====================================================
          🌟 Popular Features Section
          Purpose:
          - Links homepage authority to feature landing pages
          - Helps visitors quickly understand core Let's Go Vote capabilities
          - Strengthens SEO internal linking for feature pages
      ===================================================== */}
      <section className="container-fluid py-5">
        <div className="container">
          <div className="row text-center mb-4">
            <div className="col-12">
              <h4 className="fw-bold">Popular Features</h4>
              <p className="text-muted mb-0">
                Tools that help organizers run smoother, faster, and more engaging events.
              </p>
            </div>
          </div>

          <div className="row g-3 text-center">
            {[
              {
                icon: "📱",
                title: "QR Code Voting",
                path: "/qr-code-voting",
              },
              {
                icon: "🏆",
                title: "People's Choice Voting",
                path: "/peoples-choice-voting",
              },
              {
                icon: "🗳️",
                title: "Ranked Choice Voting",
                path: "/ranked-choice-voting",
              },
              {
                icon: "🧮",
                title: "Judged Scorecards",
                path: "/judged-scorecards",
              },
              {
                icon: "🤝",
                title: "Volunteer Management",
                path: "/volunteer-management",
              },
              {
                icon: "⭐",
                title: "Sponsor Management",
                path: "/sponsor-management",
              },
            ].map((item) => (
              <div className="col-6 col-md-4 col-lg-2" key={item.path}>
                <Link
                  to={item.path}
                  className="card h-100 border-0 shadow-sm p-3 text-decoration-none text-dark"
                >
                  <div className="display-6">{item.icon}</div>
                  <div className="fw-bold mt-2">{item.title}</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-fluid py-5">
        <div className="container">
          <div className="row text-center mb-4">
            <div className="col-12">
              <h4>How It Works</h4>
            </div>
          </div>

          <div className="row text-center g-3">
            <div className="col-md-3">
              <h2>1️⃣</h2>
              <h5>Choose Plan</h5>
              <p className="text-muted small">
                Pick the event package that fits your size, voting needs, and support level.
              </p>
            </div>

            <div className="col-md-3">
              <h2>2️⃣</h2>
              <h5>Set Up Event</h5>
              <p className="text-muted small">
                Add your event details, categories, entries, sponsors, volunteers, and voting options.
              </p>
            </div>

            <div className="col-md-3">
              <h2>3️⃣</h2>
              <h5>Launch Voting</h5>
              <p className="text-muted small">
                Print QR codes, invite attendees, and let voters participate from their phones.
              </p>
            </div>

            <div className="col-md-3">
              <h2>4️⃣</h2>
              <h5>Reveal Results</h5>
              <p className="text-muted small">
                View live results, export totals, announce winners, and keep a clean voting record.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Info Boxes Section */}
      <section id="info" className="container-fluid">
        <div className="container">
          <div className="row">
            <div className="col-xs-12 row-title text-center">
              <h4>Ready to go in seconds</h4>
            </div>
          </div>
          <div className="row text-center g-3">
            {[1, 2, 3, 4].map((id) => {
              const data = [
                { icon: "business", title: "Easy for voters" },
                { icon: "transport", title: "Real-time results" },
                { icon: "technology", title: "Built for events" },
                { icon: "search", title: "No paper needed" }
              ][id - 1];

              return (
                <div className="col-12 col-sm-6 col-md-3" key={id}>
                  <div
                    className={`info-box ${activeInfo === id ? 'opened-info' : ''}`}
                    onClick={() => setActiveInfo(id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="info-icon">
                      <i className={`hroc hroc-${data.icon}`}></i>
                    </div>
                    <div className="info-title">{data.title}</div>
                    <div className="info-circle"><div className="circle-icon"></div></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {infoPanels.map(({ id, icon, title, text }) => (
        (isMobile || activeInfo === id) && (
          <section key={id} className="container-fluid info-text-holder">
            <div className="container">
              <div className="row">
                <div className="col-xs-12 text-center">
                  <div className="info-title-icon">
                    <i className={`hroc hroc-${icon}`}></i>
                    <div>{title}</div>
                  </div>
                  <div className="info-text">{text}</div>
                </div>
              </div>
            </div>
          </section>
        )
      ))}

      {/* Features Section */}
      <section id="features" className="container-fluid">
        <div className="container">
          <div className="row">
            <div className="col-xs-12 row-title text-center">
              <h4>Built for real-world events</h4>
              <h5>Everything you need to run fair, fast, and engaging public voting</h5>
            </div>
          </div>

          {features.map((item, i) => (
            <div className="row feature-row" key={i}>
              <div className={`col-sm-12 col-md-6 ${i % 2 === 1 ? "order-md-2" : ""} text-center`}>
                <img className="feature-image" src={item.img} alt={item.title} />
              </div>
              <div className="col-sm-12 col-md-6">
                <div className="feature-title">{item.title}</div>
                <div className="feature-subtitle">{item.subtitle}</div>
                <div className="feature-text">
                  {item.text.map((t, idx) => <p key={idx}>{t}</p>)}
                </div>
              </div>
            </div>
          ))}

        </div>
      </section>

      {/* Reasons Section */}
      <section id="reasons" className="container-fluid">
        <div className="container">
          <div className="row row-title text-center">
            <div className="col-xs-12">
              <h4>More reasons why you’ll love using our platform</h4>
              <p className="text-white-50">We help make your event smoother, simpler, and more engaging — from setup to final vote count.</p>
            </div>
          </div>

          <div className="row">
            <div className="col-xs-12 col-md-6">
              {reasonsList.map(({ title, description }, i) => (
                <div className="reason-box" key={i}>
                  <h5>{title}</h5>
                  <p>{description}</p>
                </div>
              ))}
            </div>

            <div className="col-xs-12 col-md-6 text-center">
              <div className="embed-responsive embed-responsive-16by9">

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="container-fluid">
        <div className="container">
          <div className="row text-center">
            <div className="col-xs-12 row-title">
              <h4>What People Are Saying</h4>
            </div>
          </div>

          <div className="row">
            {testimonialsList.map(({ name, quote, image }, idx) => (
              <div key={idx} className="col-xs-12 col-md-4 mb-4">
                <div className="card h-100 shadow-sm border-0 text-center p-4">
                  <p className="fst-italic text-muted">“{quote}”</p>
                  <div className="fw-bold mt-2">{name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </>
  );
};

export default LandingHome;
